import React, { useState, useMemo } from 'react';
import { Card, Input, Button, Table, Space, Tag, message, Tooltip } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { exportToExcel } from '../../utils/api';

const API_BASE_URL = '/api/v1/channel-selector';

function extractDramaTitle(channelTitle = '', video = {}) {
  const desc = video.description || '';
  const ch = (channelTitle || '').trim();
  console.log(ch);

  if (!desc) return '';

  // 1. NetShort- Popular Dramas & TV：最后一个 # 之后是剧名
  if (ch.includes('NetShort- Popular Dramas & TV')) {
    const lastHash = desc.lastIndexOf('#');

    if (lastHash !== -1) {
      console.log(desc.slice(lastHash + 1).trim());
      return desc.slice(lastHash + 1).trim();
    }
  }

  // 2. StardustTV - Movies & Dramas：`Drama name:` 之后
  if (ch.includes('StardustTV - Movies & Dramas')) {
    const m = desc.match(/Drama name[:：]\s*(.+)/i);
    if (m) return m[1].trim();
  }

  // 3. iDrama - Stream Movies & TV：`Title:` 之后
  if (ch.includes('iDrama - Stream Movies & TV')) {
    const m = desc.match(/Title[:：]\s*(.+)/i);
    if (m) return m[1].trim();
  }

  // 4. SnackShort Official - GET APP NOW ON IOS & ANDROID：【】内
  if (ch.includes('SnackShort Official - GET APP NOW ON IOS & ANDROID')) {
    const m = desc.match(/【([^】]+)】/);
    if (m) return m[1].trim();
  }

  // 5. StarShort APP - GET APP NOW ON IOS & ANDROID：`Drama Title:` 之后
  if (ch.includes('StarShort APP - GET APP NOW ON IOS & ANDROID')) {
    const m = desc.match(/Drama Title[:：]\s*(.+)/i);
    if (m) return m[1].trim();
  }

  // 6. CafeDrama APP — Watch The Full Episodes On APP：`Title:` 之后
  if (ch.includes('CafeDrama APP — Watch The Full Episodes On APP')) {
    const m = desc.match(/Title[:：]\s*(.+)/i);
    if (m) return m[1].trim();
  }

  // 通用兜底：先尝试 Drama name / Drama Title / Title，再尝试【】
  let m = desc.match(/Drama (?:name|Title)[:：]\s*(.+)/i);
  if (m) return m[1].trim();

  m = desc.match(/Title[:：]\s*(.+)/i);
  if (m) return m[1].trim();

  m = desc.match(/【([^】]+)】/);
  if (m) return m[1].trim();

  return '';
}

async function fetchChannelVideos(input) {
  const url = input.trim();
  let qs = '';

  // 如果是完整的 YouTube 链接，则走 url 参数，由后端解析 channelId
  if (url.startsWith('http://') || url.startsWith('https://')) {
    qs = `url=${encodeURIComponent(url)}`;
  } else {
    // 否则当作 channelId 处理
    qs = `id=${encodeURIComponent(url)}`;
  }

  const resp = await fetch(`${API_BASE_URL}/channel-videos?${qs}`);
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.message || '获取频道视频失败');
  }
  const body = await resp.json();
  if (!body.success) {
    throw new Error(body.message || '获取频道视频失败');
  }
  return body.data;
}

export default function ChannelVideos() {
  const [channelInput, setChannelInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ channelTitle: '', videos: [], total: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const handleSearch = async () => {
    if (!channelInput.trim()) {
      return message.warning('请先输入 YouTube 频道链接或频道 ID');
    }
    setLoading(true);
    try {
      const result = await fetchChannelVideos(channelInput);
      const rawVideos = result?.videos || [];
      const channelTitle = result.channelTitle || '';

      // 从 title/description/tags 中提取 hashtag，生成展示用标签，并根据不同频道规则提取剧名
      const videosWithTags = rawVideos.map(v => {
        const baseTags = Array.isArray(v.tags) ? v.tags.slice() : [];
        const text = `${v.title || ''} ${v.description || ''}`;
        const hashMatches = text.match(/#([\p{L}\p{N}_]+)/gu) || [];
        const hashTags = hashMatches.map(t => t.replace(/^#/, ''));
        const allTags = Array.from(new Set([...baseTags, ...hashTags])).filter(Boolean);
        const dramaTitle = extractDramaTitle(channelTitle, v);
        return { ...v, displayTags: allTags, dramaTitle };
      });

      setData({
        channelTitle,
        channelId: result.channelId || '',
        videos: videosWithTags,
        total: result.total || videosWithTags.length,
      });
      // 新数据时重置到第一页
      setPagination(p => ({ ...p, current: 1 }));
      message.success(`共加载 ${result.total || 0} 个视频`);
    } catch (e) {
      console.error(e);
      message.error(e.message || '获取频道视频失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!data.videos || !data.videos.length) {
      return message.warning('暂无可导出的视频数据');
    }
    try {
      // 导出全部数据，字段与表格列保持一致
      const rows = data.videos.map(v => ({
        标题: v.title,
        剧名: v.dramaTitle || '',
        标签: Array.isArray(v.displayTags) ? v.displayTags.join(',') : '',
        发布时间: v.publishedAt ? new Date(v.publishedAt).toLocaleString() : '',
        时长_秒: v.duration,
        播放量: v.viewCount,
        点赞: v.likeCount,
        评论: v.commentCount,
        链接: `https://www.youtube.com/watch?v=${v.videoId}`,
      }));
      await exportToExcel(rows, `${data.channelTitle}-${data.channelId}-${new Date().toISOString()}.xlsx`);
      message.success(`导出成功，共 ${rows.length} 条数据`);
    } catch (e) {
      console.error(e);
      message.error(e.message || '导出失败');
    }
  };

  const pagedVideos = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return data.videos.slice(start, end);
  }, [data.videos, pagination.current, pagination.pageSize]);

  const columns = [
    {
      title: '封面',
      dataIndex: 'thumbnails',
      width: 120,
      render: (thumbs, record) => {
        const url = thumbs?.medium || thumbs?.high || thumbs?.default || '';
        if (!url) return null;
        return (
          <img
            src={url}
            alt={record.title}
            style={{ width: 150, cursor: 'pointer' }}
            onClick={() => window.open(`https://www.youtube.com/watch?v=${record.videoId}`, '_blank')}
          />
        );
      },
    },
    {
      title: '标签',
      dataIndex: 'displayTags',
      width: 260,
      render: (tags = []) => (
        <Space wrap>
          <Tooltip title={tags.join(',')}>
            {tags && tags.length > 10 && (
              <>
                {tags.slice(0, 10).map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
                <Tag>...</Tag>
              </>
            )}
            {tags && tags.length <= 10 && tags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 320,
    },
    {
      title: '剧名',
      dataIndex: 'dramaTitle',
      width: 260,
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAt',
      width: 180,
      render: (v) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: '时长(秒)',
      dataIndex: 'duration',
      width: 100,
    },
    {
      title: '播放量',
      dataIndex: 'viewCount',
      width: 120,
    },
    {
      title: '点赞',
      dataIndex: 'likeCount',
      width: 100,
    },
    {
      title: '评论',
      dataIndex: 'commentCount',
      width: 100,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="YouTube 频道视频列表"
        extra={
          <Space>
            <Input
              placeholder="输入频道链接或 channelId，例如 https://www.youtube.com/@Playlet_app/videos 或 UC5iaTptzxnYzJlgY7mKbljA"
              style={{ width: 360 }}
              value={channelInput}
              onChange={e => setChannelInput(e.target.value)}
              onPressEnter={handleSearch}
            />
            <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={handleSearch}>
              获取视频
            </Button>
            <Button type="default" icon={<DownloadOutlined />} onClick={handleExport} disabled={!data.videos.length}>
              导出Excel
            </Button>
          </Space>
        }
      >
        {data.channelTitle && (
          <div style={{ marginBottom: 16 }}>
            当前频道：<strong>{data.channelTitle}</strong>（{data.channelId}），共 {data.total} 个视频
          </div>
        )}
        <Table
          rowKey={r => r.videoId}
          dataSource={pagedVideos}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data.videos.length,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
}


